import React, { useEffect, useReducer, useState } from "react";
import { faSquare, faSquareCheck } from "@fortawesome/free-regular-svg-icons";

import {
  DividingContainer,
  FieldSet,
  HelpText,
  NavContainer,
  PanelButton,
  PanelButtonGroup,
  Prompt,
  Input,
} from "./UI";
import { NextButton } from "../UI";
import { useSurveyStore } from "../../Stores/SurveyStore";
import { observer } from "mobx-react";
import { AccessSelector } from ".";
import { useRootStore } from "../../Stores/RootStore";
import { useNavigation } from "../../Providers/NavigationProvider";
import { useModules } from "../../Hooks/useModules";

function DestinationAccess() {
  const { rootState } = useRootStore();
  const surveyStore = useSurveyStore();
  const [nextDisabled, setNextDisabled] = useState(true);
  const { navigate } = useNavigation();
  useModules();

  const [currentPrompt, setCurrentPrompt] = useReducer(
    currentPromptReducer,
    null
  );

  const closePrompt = () => {
    // slice and dice currentPrompt to get surveyStore access value
    let field = currentPrompt ? currentPrompt.split("-")[0] : "";
    field = field.charAt(0).toUpperCase() + field.slice(1);

    if (field && surveyStore[`destination${field}`] === "other") {
      surveyStore[`setDestination${field}`]("");
      setCurrentPrompt(field.toLowerCase() + "-other");
    } else if (field && surveyStore[`destination${field}`] === "stairs") {
      setCurrentPrompt(field.toLowerCase() + "-stairs");
    } else if (field && surveyStore[`destination${field}`] === "elevator") {
      setCurrentPrompt(field.toLowerCase() + "-elevator");
    } else {
      setCurrentPrompt(null);
    }
  };

  const setDestinationAccess = (e) => {
    const field = e.target.name;
    const action = `setDestination${field}`;
    if (surveyStore[action]) {
      surveyStore[action](e.target.value);
    }
  };

  const validInput = (data) => {
    return data.trim() !== "";
  };

  useEffect(() => {
    if (
      validInput(surveyStore.destinationIndoor) &&
      validInput(surveyStore.destinationOutdoor) &&
      validInput(surveyStore.destinationParking)
    ) {
      setNextDisabled(false);
    } else {
      setNextDisabled(true);
    }
  }, [
    nextDisabled,
    surveyStore.destinationIndoor,
    surveyStore.destinationOutdoor,
    surveyStore.destinationParking,
  ]);

  const formOptions = {
    parking: [
      { label: "Street", name: "parking", value: "street" },
      { label: "Driveway", name: "parking", value: "driveway" },
      { label: "Car Park", name: "parking", value: "car-cark" },
      {
        label: "Parking Garage",
        name: "parking",
        value: "parking-garage",
      },
      { label: "Loading Zone", name: "parking", value: "loading-zone" },
      { label: "Loading Bay", name: "parking", value: "loading-bay" },
      { label: "Other", name: "parking", value: "other" },
    ],
    outdoor: [
      { label: "Short Walk", name: "outdoor", value: "short-walk" },
      { label: "Medium Walk", name: "outdoor", value: "medium-walk" },
      { label: "Long Walk", name: "outdoor", value: "long-walk" },
      { label: "Steep Hill", name: "outdoor", value: "steep-hill" },
      { label: "Other", name: "outdoor", value: "other" },
    ],
    indoor: [
      {
        label: "All one floor / Bungalo",
        name: "indoor",
        value: "all-one-floor",
      },
      { label: "Stairs", name: "indoor", value: "stairs" },
      {
        label: "Dedicated Lift",
        name: "indoor",
        value: "dedicated-lift",
      },
      {
        label: "Elevator",
        name: "indoor",
        value: "elevator",
        followUp: {
          question: "Can it be reserved?",
          options: ["Can be reserved", "Shared"],
        },
      },
    ],
  };

  const submitStep = async () => {
    rootState.api
      .submitDestinationAccess({
        uuid: surveyStore.surveyId,
        parking: surveyStore.destinationParking,
        "outdoor-access": surveyStore.destinationOutdoor,
        "indoor-access": surveyStore.destinationIndoor,
        stairs: surveyStore.destinationStairs,
        "indoor-elevator": surveyStore.destinationElevator,
      })
      .then((data) => {
        rootState.setModules(data.modules);
        navigate(data.modules.next);
      })
      .catch((e) => {
        rootState.setError({ message: e.message });
      });
  };

  return (
    <>
      <DividingContainer>
        <FieldSet title="Destination Access">
          <PanelButtonGroup>
            <PanelButton
              handleClick={() => setCurrentPrompt("parking")}
              headerText="Parking"
              bodyText="Parking information about location"
              complete={surveyStore.destinationParking ? true : undefined}
              icon={surveyStore.destinationParking ? faSquareCheck : faSquare}
            />
            <PanelButton
              handleClick={() => setCurrentPrompt("outdoor-access")}
              headerText="Outdoor Access"
              bodyText="The walk from the removal van to your front door"
              complete={surveyStore.destinationOutdoor ? true : undefined}
              icon={surveyStore.destinationOutdoor ? faSquareCheck : faSquare}
            />
            <PanelButton
              handleClick={() => setCurrentPrompt("indoor-access")}
              headerText="Indoor Access"
              bodyText="Getting around inside your home"
              complete={surveyStore.destinationIndoor ? true : undefined}
              icon={surveyStore.destinationIndoor ? faSquareCheck : faSquare}
            />
          </PanelButtonGroup>
        </FieldSet>

        <HelpText>
          Please select items in each category that are required for your move
        </HelpText>
        <NavContainer>
          <NextButton
            disabled={nextDisabled}
            handleSubmit={submitStep}
          ></NextButton>
        </NavContainer>
      </DividingContainer>

      <AccessSelectorSection
        currentPrompt={currentPrompt}
        setCurrentPrompt={setCurrentPrompt}
        closePrompt={closePrompt}
        setDestinationAccess={setDestinationAccess}
        formOptions={formOptions}
      />
    </>
  );
}

const AccessSelectorSection = observer(
  ({
    currentPrompt,
    setCurrentPrompt,
    closePrompt,
    setDestinationAccess,
    formOptions,
  }) => {
    const surveyStore = useSurveyStore();
    switch (currentPrompt) {
      case "parking":
        return (
          <AccessSelector
            formLabel="Parking"
            access={surveyStore.destinationParking}
            setAccess={setDestinationAccess}
            closePrompt={closePrompt}
            formOptions={formOptions.parking}
          />
        );
      case "outdoor-access":
        return (
          <AccessSelector
            formLabel="Outdoor"
            access={surveyStore.destinationOutdoor}
            setAccess={setDestinationAccess}
            closePrompt={closePrompt}
            formOptions={formOptions.outdoor}
          />
        );
      case "indoor-access":
        return (
          <AccessSelector
            formLabel="Indoor"
            access={surveyStore.destinationIndoor}
            setAccess={setDestinationAccess}
            closePrompt={closePrompt}
            formOptions={formOptions.indoor}
          />
        );
      case "indoor-stairs":
        return (
          <Prompt
            singleBtn
            leftBtnText="Done"
            closeBtnClick={() => setCurrentPrompt(null)}
            leftBtnClick={() => setCurrentPrompt(null)}
          >
            <>
              <p>How many flights of stairs to reach your floor?</p>
              <Input
                type="number"
                placeholder="Number of flights"
                value={surveyStore.destinationStairs}
                handleChange={(e) => {
                  surveyStore.setDestinationStairs(e.target.value);
                }}
              />
            </>
          </Prompt>
        );
      case "indoor-elevator":
        return (
          <Prompt
            leftBtnText="Shared"
            rightBtnText="Can be reserved"
            closeBtnClick={() => setCurrentPrompt(null)}
            rightBtnClick={() => {
              setCurrentPrompt(null);
              surveyStore.setDestinationElevator("can-be-reserved");
            }}
            leftBtnClick={() => {
              setCurrentPrompt(null);
              surveyStore.setDestinationElevator("shared");
            }}
          >
            <>
              <p>Can the elevator be reserved or is it shared?</p>
            </>
          </Prompt>
        );
      case "parking-other":
        return (
          <Prompt
            singleBtn
            leftBtnText="Done"
            closeBtnClick={() => setCurrentPrompt(null)}
            leftBtnClick={() => setCurrentPrompt(null)}
          >
            <>
              <p>Please describe the parking situation.</p>
              <Input
                value={surveyStore.DestinationParking}
                handleChange={(e) =>
                  surveyStore.setDestinationParking(e.target.value)
                }
              />
            </>
          </Prompt>
        );
      case "outdoor-other":
        return (
          <Prompt
            singleBtn
            leftBtnText="Done"
            closeBtnClick={() => setCurrentPrompt(null)}
            leftBtnClick={() => setCurrentPrompt(null)}
          >
            <>
              <p>Please describe the outdoor access situation.</p>

              <Input
                value={surveyStore.DestinationOutdoor}
                handleChange={(e) =>
                  surveyStore.setDestinationOutdoor(e.target.value)
                }
              />
            </>
          </Prompt>
        );
      default:
        return null;
    }
  }
);

export default observer(DestinationAccess);

function currentPromptReducer(currentPrompt, prompt) {
  switch (prompt) {
    case "parking":
    case "parking-other":
    case "indoor-access":
    case "indoor-stairs":
    case "indoor-elevator":
    case "outdoor-access":
    case "outdoor-other":
      return prompt;
    default:
      return null;
  }
}
